/**
 * Two raster basemaps, and the only third-party request Easting makes.
 *
 * The privacy page says "No analytics scripts or tracking cookies on this
 * site" and that promise is a differentiator, so the one place the browser
 * talks to somebody else has to be named rather than discovered. Esri World
 * Imagery is what a land professional wants under a parcel; OSM is the
 * fallback when imagery is stale or the parcel is rural enough that roads
 * read better than trees.
 *
 * Raster rather than vector: no API key, no account, no per-tile billing
 * relationship, and nothing that has to be rotated. Attribution is required
 * by both and rendered by MapLibre from these definitions.
 */

import type { StyleSpecification } from "maplibre-gl";

export type BasemapId = "hybrid" | "imagery" | "osm";

/** What the map opens on. Imagery alone is beautiful and hard to navigate: a
 *  land professional locating a parcel needs the road and the street name to
 *  know they are in the right block, and the labels are what turn a green
 *  rectangle into a place. */
export const DEFAULT_BASEMAP: BasemapId = "hybrid";

const ESRI_IMAGERY =
  "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
// Esri's reference layers: roads, then boundaries with place names, both on
// transparency and designed to sit over the imagery.
const ESRI_TRANSPORT =
  "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}";
const ESRI_REFERENCE =
  "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTRIBUTION =
  "Imagery &copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community";

export const BASEMAPS: Record<
  BasemapId,
  { label: string; style: StyleSpecification }
> = {
  hybrid: {
    label: "Hybrid",
    style: {
      version: 8,
      sources: {
        imagery: {
          type: "raster",
          tiles: [ESRI_IMAGERY],
          tileSize: 256,
          maxzoom: 19,
          attribution: ESRI_ATTRIBUTION,
        },
        transport: {
          type: "raster",
          tiles: [ESRI_TRANSPORT],
          tileSize: 256,
          maxzoom: 19,
        },
        reference: {
          type: "raster",
          tiles: [ESRI_REFERENCE],
          tileSize: 256,
          maxzoom: 19,
        },
      },
      layers: [
        { id: "imagery", type: "raster", source: "imagery" },
        { id: "transport", type: "raster", source: "transport" },
        { id: "reference", type: "raster", source: "reference" },
      ],
    },
  },
  imagery: {
    label: "Imagery",
    style: {
      version: 8,
      sources: {
        imagery: {
          type: "raster",
          tiles: [
            "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          ],
          tileSize: 256,
          maxzoom: 19,
          attribution:
            "Imagery &copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community",
        },
      },
      layers: [{ id: "imagery", type: "raster", source: "imagery" }],
    },
  },
  osm: {
    label: "Map",
    style: {
      version: 8,
      sources: {
        osm: {
          type: "raster",
          tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
          tileSize: 256,
          maxzoom: 19,
          attribution: "&copy; OpenStreetMap contributors",
        },
      },
      layers: [{ id: "osm", type: "raster", source: "osm" }],
    },
  },
};
